/**
 * ReviewHub step 2: evidence (ADR-0023, "Step 2 design", proposed 2026-10-29).
 *
 * Pure functions shared by the API and the browser: where each measure comes from, the parsers
 * for reports that have no API (ported from the TBR Builder's exports_parse.py and sat_parse.py),
 * the figures DocVault can work out from its own records, and the collector's checks.
 * The browser reads the uploaded file (read-excel-file, pdf.js) and runs the parser; the API runs
 * the same parser checks again on what it receives and keeps the original file with the review.
 */
import type { MetricValue } from "./reviewhub.js";

export type SourceKind = "atera" | "m365" | "dns" | "techhub" | "review" | "upload" | "techprep" | "owner";
export const SOURCE_LABEL: Record<SourceKind, string> = {
  atera: "Atera", m365: "Microsoft 365", dns: "Public DNS", techhub: "TechHub records", review: "Last review",
  upload: "Upload", techprep: "Tech Prep", owner: "Owner",
};
export type UploadKind = "sla" | "auditor" | "learning" | "phishing" | "backup";
export const UPLOAD_LABEL: Record<UploadKind, string> = {
  sla: "Atera SLA report", auditor: "Atera Auditor patch export", learning: "Huntress Managed Learning",
  phishing: "Huntress Managed Phishing", backup: "Backup report",
};

/** Where each measure comes from in step 2. `until` = the step that moves it to an automatic pull. */
export const MEASURE_SOURCE: Record<string, { source: SourceKind; upload?: UploadKind; until?: 3 | 4 }> = {
  sla_pct: { source: "upload", upload: "sla" },
  crit_patch_pct: { source: "upload", upload: "auditor" },
  csat: { source: "techprep" },               // Atera's survey summary: confirm on the live API before pulling
  recurring_open: { source: "techprep" },     // Atera category rollup is partial (TBR source mapping)
  recurring_over_90: { source: "techprep" },
  mfa_pct: { source: "m365" },
  edr_pct: { source: "techprep", until: 4 },
  training_pct: { source: "upload", upload: "learning" },
  dmarc: { source: "dns" },
  crit_vuln_30_90: { source: "techprep", until: 3 },
  crit_vuln_over_90: { source: "techprep", until: 3 },
  open_incidents: { source: "techprep", until: 4 },
  legacy_auth: { source: "m365" },
  critical_unprotected: { source: "techprep" },
  offsite_copy: { source: "techprep" },
  immutable_copy: { source: "techprep" },
  restore_test_days: { source: "techprep" },
  last_test_failed: { source: "techprep" },
  rto_rpo_met: { source: "techprep" },
  past_life_pct: { source: "techhub" },
  eos_infra: { source: "techhub" },
  eos_12mo_no_plan: { source: "techhub" },
  unsupported_os: { source: "techhub" },
  replacement_plan: { source: "review" },
  secure_score: { source: "m365" },
  license_waste_pct: { source: "m365" },
  data_governance: { source: "techprep" },
  obligations_identified: { source: "owner", until: 3 },
  assessment_months: { source: "owner", until: 3 },
  open_high_gaps: { source: "owner", until: 3 },
  open_moderate_gaps: { source: "owner", until: 3 },
  attestation_at_risk: { source: "owner", until: 3 },
  roadmap_exists: { source: "review" },
  commitments_met_pct: { source: "review" },
  forecast_months: { source: "owner" },       // typed on the scorecard: nothing fills it from the last review
};
export const TECH_PREP_KEYS = Object.entries(MEASURE_SOURCE).filter(([, s]) => s.source === "techprep").map(([k]) => k);

/** One figure found by a pull or read from an upload. */
export interface Found { key: string; value: MetricValue; source: SourceKind; detail: string; upload?: UploadKind }
export type CheckStatus = "pass" | "flag" | "fail" | "later";
export interface Check { key: string; status: CheckStatus; title: string; detail: string }

const r1 = (n: number) => Math.round(n * 10) / 10;
const pct = (num: number, den: number) => (den ? r1((100 * num) / den) : null);
/** Same as tbr_common.norm: device names disagree on case and hyphens between consoles. */
export const normName = (s: unknown) => String(s ?? "").toLowerCase().replace(/\s*\(.*$/, "").replace(/[^a-z0-9]/g, "");

// ---------------------------------------------------------------- uploads: Atera SLA report (.xlsx, Summary sheet)
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
function isoFromWords(s: string): string | null {
  const m = /^([A-Z][a-z]+)[_ ](\d{1,2})[_ ,]+(\d{4})$/.exec(s.trim());
  if (!m) return null;
  const mi = MONTHS.findIndex((x) => x.toLowerCase().startsWith(m[1]!.toLowerCase().slice(0, 3)));
  if (mi < 0) return null;
  return `${m[3]}-${String(mi + 1).padStart(2, "0")}-${m[2]!.padStart(2, "0")}`;
}
/** The date range Atera writes into the export's file name, e.g. "SLA Report Jul_1_2026-Sep_30_2026.xlsx". */
export function slaRangeFromName(name: string): { from: string; to: string } | null {
  const m = /([A-Z][a-z]+_\d{1,2}_\d{4})-([A-Z][a-z]+_\d{1,2}_\d{4})/.exec(name);
  if (!m) return null;
  const from = isoFromWords(m[1]!), to = isoFromWords(m[2]!);
  return from && to ? { from, to } : null;
}
export interface SlaParsed { tickets: number; responded: number; in_sla: number; pct: number | null; range: { from: string; to: string } | null }
/** rows = the Summary sheet: header row, then the values row. */
export function parseSla(rows: unknown[][], fileName: string): SlaParsed {
  if (rows.length < 2) throw new Error("This doesn't look like an Atera SLA report: the Summary sheet is empty.");
  const hdr = rows[0]!.map((h) => String(h ?? "").trim());
  const val = rows[1]!;
  const get = (k: string) => { const i = hdr.indexOf(k); return i >= 0 ? Number(val[i] ?? 0) || 0 : NaN; };
  const responded = get("Tickets Responded"), inSla = get("Responded In SLA");
  if (Number.isNaN(responded) || Number.isNaN(inSla)) throw new Error("This doesn't look like an Atera SLA report: no Tickets Responded and Responded In SLA columns.");
  const tickets = get("Tickets");
  return { tickets: Number.isNaN(tickets) ? 0 : tickets, responded, in_sla: inSla, pct: pct(inSla, responded), range: slaRangeFromName(fileName) };
}

// ---------------------------------------------------------------- uploads: Atera Auditor report (.xlsx, Auditor sheet)
export interface AuditorDevice { name: string; key: string; role: "server" | "workstation"; os: string; last_seen: string; customer: string; critical_missing: number | null }
export interface AuditorParsed { devices: AuditorDevice[]; with_critical: number; counted: number; pct: number | null; customers: string[] }
export function parseAuditor(rows: unknown[][]): AuditorParsed {
  if (rows.length < 2) throw new Error("This doesn't look like an Atera Auditor report: the Auditor sheet is empty.");
  const hdr = rows[0]!.map((h) => String(h ?? "").trim());
  const ix = (k: string) => hdr.indexOf(k);
  if (ix("Device Name") < 0) throw new Error("This doesn't look like an Atera Auditor report: no Device Name column.");
  const at = (r: unknown[], k: string) => (ix(k) >= 0 ? String(r[ix(k)] ?? "") : "");
  const devices: AuditorDevice[] = [];
  for (const r of rows.slice(1)) {
    const raw = at(r, "Device Name");
    if (!raw.trim()) continue;
    const t = at(r, "OS Type");
    const cp = ix("Critical Patch") >= 0 ? Number(r[ix("Critical Patch")] ?? 0) || 0 : null;
    devices.push({ name: raw.split(/\s*\(/)[0]!.trim(), key: normName(raw), role: /Server|Domain Controller/.test(t) ? "server" : "workstation",
      os: at(r, "OS Edition"), last_seen: at(r, "Last Seen"), customer: at(r, "Customer Name"), critical_missing: cp });
  }
  const counted = devices.filter((d) => d.critical_missing !== null);
  const clean = counted.filter((d) => d.critical_missing === 0).length;
  return { devices, with_critical: counted.length - clean, counted: counted.length, pct: pct(clean, counted.length),
    customers: [...new Set(devices.map((d) => d.customer).filter(Boolean))] };
}

// ---------------------------------------------------------------- uploads: Huntress Managed Learning / Phishing (PDF text)
const EMAIL = /[A-Za-z0-9._%+'-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
/** Whitespace-blind: PDF text letter-spaces small-caps headings ("C O M P L E T E D"). */
const sp = (phrase: string) => phrase.replace(/ /g, "").split("").map(esc).join("\\s*");
const num = (re: RegExp, text: string) => { const m = re.exec(text); return m ? Number(m[1]) : null; };
function sectionEmails(text: string, start: string, stops: string[]): string[] {
  const m = new RegExp(sp(start), "i").exec(text);
  if (!m) return [];
  let chunk = text.slice(m.index + m[0].length);
  for (const w of stops) { const j = new RegExp(sp(w), "i").exec(chunk); if (j && j.index > 0) chunk = chunk.slice(0, j.index); }
  return [...new Set((chunk.match(EMAIL) ?? []).map((e) => e.toLowerCase()))].sort();
}
export function pdfKind(text: string): "learning" | "phishing" | null {
  if (/Huntress Managed Learning/.test(text)) return "learning";
  if (/Huntress Managed Phishing/.test(text)) return "phishing";
  return null;
}
export interface LearningParsed { month: string | null; enrolled: number | null; completed: number | null; avg_score: number | null; completed_emails: string[]; incomplete_emails: string[] }
export function parseLearning(text: string): LearningParsed {
  const month = /Huntress Managed Learning\s+([A-Z][a-z]+ \d{4})/.exec(text);
  const summary = new RegExp(`${sp("LEARNERS ENROLLED")}.*?${sp("COMPLETED")}.*?${sp("AVERAGE SCORE")}\\s+(\\d+)\\s+(\\d+)\\s+(\\d+)%`, "is").exec(text);
  return {
    month: month ? month[1]! : null,
    enrolled: summary ? Number(summary[1]) : num(/out of (\d+)/i, text),
    completed: summary ? Number(summary[2]) : num(/Completion:\s*(\d+)\s+out of/i, text),
    avg_score: summary ? Number(summary[3]) : num(/Avg\. score:\s*(\d+)%/i, text),
    completed_emails: sectionEmails(text, "COMPLETED LEARNERS", ["INCOMPLETE LEARNERS", "EPISODE 2 OF"]),
    incomplete_emails: sectionEmails(text, "INCOMPLETE LEARNERS", ["EPISODE 2 OF", "COMPLETED LEARNERS"]),
  };
}
export interface PhishingParsed { month: string | null; learners: number | null; scenarios: number | null; delivered: number | null; opened: number | null; clicked: number | null; compromised: number | null; reported: number | null }
export function parsePhishing(text: string): PhishingParsed {
  const month = /Huntress Managed Phishing\s+([A-Z][a-z]+ \d{4})/.exec(text);
  const head = new RegExp(`${sp("LEARNERS")}\\s+${sp("SCENARIOS")}.*?\\n\\s*(\\d+)\\s+(\\d+)`, "is").exec(text);
  const first = text.split("Scenario 1")[0]!;
  const c = (w: string) => num(new RegExp(`^\\s*${w}\\s+(\\d+)\\s*$`, "im"), first) ?? num(new RegExp(`${w}\\s+(\\d+)`, "i"), first);
  return { month: month ? month[1]! : null, learners: head ? Number(head[1]) : null, scenarios: head ? Number(head[2]) : null,
    delivered: num(/Delivered\s+(\d+)/i, first), opened: c("Opened"), clicked: c("Clicked"), compromised: c("Compromised"), reported: c("Reported") };
}

// ---------------------------------------------------------------- derived from TechHub records
/** Planned life in years (Rating Definitions v1.0 note: workstations and servers 5, network 5 to 7, access points 5). */
export const LIFE_YEARS: Record<string, number> = { workstation: 5, laptop: 5, server: 5, nas: 5, access_point: 5, firewall: 7, router: 7, switch: 7 };
export interface AssetIn { name: string; kind: string; os: string; lifecycle: string; purchased_on: string | null; warranty_end: string | null; last_seen_at: string | null; source_system: string }
const addYears = (iso: string, n: number) => `${Number(iso.slice(0, 4)) + n}${iso.slice(4, 10)}`;
export function pastLife(assets: AssetIn[], today: string) {
  const inScope = assets.filter((a) => a.lifecycle === "active" && LIFE_YEARS[a.kind]);
  const past: string[] = [], unknown: string[] = [];
  for (const a of inScope) {
    const p = a.purchased_on?.slice(0, 10), w = a.warranty_end?.slice(0, 10);
    if (p) { if (addYears(p, LIFE_YEARS[a.kind]!) <= today) past.push(a.name); }
    else if (w) { if (w < today) past.push(a.name); }
    else unknown.push(a.name);
  }
  const known = inScope.length - unknown.length;
  return { pct: pct(past.length, known), past, known, unknown };
}
/** Microsoft end-of-support dates for the operating systems that matter here (extended support). */
const OS_EOS: { re: RegExp; label: string; ends: string }[] = [
  { re: /windows\s*server\s*2008/i, label: "Windows Server 2008", ends: "2020-01-14" },
  { re: /windows\s*server\s*2012/i, label: "Windows Server 2012", ends: "2023-10-10" },
  { re: /windows\s*server\s*2016/i, label: "Windows Server 2016", ends: "2027-01-12" },
  { re: /windows\s*server\s*2019/i, label: "Windows Server 2019", ends: "2029-01-09" },
  { re: /windows\s*server\s*2022/i, label: "Windows Server 2022", ends: "2031-10-14" },
  { re: /windows\s*(7|8(\.1)?|xp|vista)\b/i, label: "Windows 7/8", ends: "2023-01-10" },
  { re: /windows\s*10\b/i, label: "Windows 10", ends: "2025-10-14" },
];
export const osEndOfSupport = (os: string) => OS_EOS.find((o) => o.re.test(os)) ?? null;
export function lifecycleFromAssets(assets: AssetIn[], today: string, roadmapText: string) {
  const active = assets.filter((a) => a.lifecycle === "active");
  const ws = active.filter((a) => ["workstation", "laptop"].includes(a.kind));
  const srv = active.filter((a) => ["server", "virtual"].includes(a.kind));
  const unsupported = ws.filter((a) => { const e = osEndOfSupport(a.os); return e && e.ends <= today; });
  const eosNow = srv.filter((a) => { const e = osEndOfSupport(a.os); return e && e.ends <= today; });
  const yearOut = addYears(today, 1);
  const road = roadmapText.toLowerCase();
  const soon = srv.filter((a) => { const e = osEndOfSupport(a.os); return e && e.ends > today && e.ends <= yearOut; });
  const planned = soon.filter((a) => road.includes(a.name.toLowerCase()) || (road.includes("server") && road.includes(osEndOfSupport(a.os)!.label.slice(-4))));
  return { unsupported, eosNow, soon, noPlan: soon.filter((a) => !planned.includes(a)), planned };
}

// ---------------------------------------------------------------- Microsoft 365 licenses, DMARC
export function licenseWaste(lic: { sku_part: string; bought: number; assigned: number }[], free: Set<string>) {
  const paid = lic.filter((l) => !free.has(l.sku_part) && l.bought > 0);
  const bought = paid.reduce((s, l) => s + l.bought, 0), assigned = paid.reduce((s, l) => s + Math.min(l.assigned, l.bought), 0);
  return { pct: pct(bought - assigned, bought), bought, assigned };
}
/** DMARC record → the measure's values: reject, quarantine, none (published at p=none), missing. */
export function dmarcLevel(txt: string[]): "reject" | "quarantine" | "none" | "missing" {
  const rec = txt.map((t) => t.trim()).find((t) => /^v=DMARC1/i.test(t));
  if (!rec) return "missing";
  const p = /(?:^|;)\s*p\s*=\s*(\w+)/i.exec(rec)?.[1]?.toLowerCase();
  return p === "reject" ? "reject" : p === "quarantine" ? "quarantine" : "none";
}
export const hasSpf = (txt: string[]) => txt.some((t) => /^v=spf1/i.test(t.trim()));

// ---------------------------------------------------------------- the collector's checks
export interface CheckInput {
  today: string; period: { start: string; end: string };
  ticketsScoped?: { total: number; foreign: number } | null;
  sla?: SlaParsed | null; auditor?: AuditorParsed | null; clientNames?: string[];
  assets: AssetIn[]; learning: LearningParsed[]; phishing: PhishingParsed[];
  licensedPeople?: { name: string; email: string }[] | null;
}
export function runChecks(x: CheckInput): Check[] {
  const out: Check[] = [];
  const add = (key: string, status: CheckStatus, title: string, detail: string) => out.push({ key, status, title, detail });
  if (x.ticketsScoped) {
    const { total, foreign } = x.ticketsScoped;
    add("tickets_scoped", foreign ? "fail" : "pass", "Tickets are this client's only",
      foreign ? `${foreign} of ${total} tickets Atera returned belong to other customers. They were left out; check the customer match in Settings > Integrations.`
        : `All ${total} tickets carry this client's Atera customer.`);
  }
  if (x.sla) {
    const r = x.sla.range;
    const covers = r && r.from <= x.period.start && r.to >= x.period.end;
    add("sla_dated", !r ? "fail" : covers ? "pass" : "flag", "The SLA export covers the quarter",
      !r ? "The file name has no date range. Re-run the SLA report with the quarter's dates; an undated figure can't be shown as this quarter's."
        : covers ? `The export runs ${r.from} to ${r.to}.` : `The export runs ${r.from} to ${r.to}; the quarter is ${x.period.start} to ${x.period.end}.`);
  }
  if (x.auditor && x.clientNames?.length) {
    const others = x.auditor.customers.filter((c) => !x.clientNames!.some((n) => normName(n) === normName(c)));
    if (others.length) add("auditor_scoped", "fail", "The Auditor export is this client's only", `It also lists ${others.slice(0, 3).join(", ")}${others.length > 3 ? " and others" : ""}. Export it for this client only.`);
  }
  const managed = x.assets.filter((a) => a.lifecycle === "active" && ["workstation", "laptop", "server"].includes(a.kind));
  const fromRmm = managed.filter((a) => a.source_system !== "manual");
  if (fromRmm.length) {
    const cutoff = new Date(Date.parse(`${x.today}T12:00:00Z`) - 30 * 864e5).toISOString();
    const quiet = fromRmm.filter((a) => !a.last_seen_at || a.last_seen_at < cutoff);
    add("agents_reporting", quiet.length ? "flag" : "pass", "Agents reporting",
      quiet.length ? `${quiet.length} of ${fromRmm.length} devices haven't checked in for 30 days: ${quiet.slice(0, 5).map((a) => a.name).join(", ")}${quiet.length > 5 ? "…" : ""}. Fix, retire in TechHub, or note why.`
        : `All ${fromRmm.length} devices checked in within 30 days.`);
    const notManaged = managed.filter((a) => a.source_system === "manual");
    add("under_management", notManaged.length ? "flag" : "pass", "Devices under management",
      notManaged.length ? `${notManaged.length} active devices in TechHub aren't in the RMM: ${notManaged.slice(0, 5).map((a) => a.name).join(", ")}.`
        : "Every active workstation and server in TechHub is in the RMM.");
  }
  const months = new Set(x.learning.map((l) => l.month).filter(Boolean));
  add("training_present", months.size >= 3 ? "pass" : months.size ? "flag" : "fail", "Training reports present",
    months.size ? `Huntress Managed Learning for ${[...months].join(", ")}${months.size < 3 ? ". The quarter has three months." : "."}` : "No Huntress Managed Learning report uploaded for the quarter.");
  if (x.learning.length && x.licensedPeople?.length) {
    const latest = x.learning[x.learning.length - 1]!;
    const enrolled = new Set([...latest.completed_emails, ...latest.incomplete_emails]);
    if (enrolled.size) {
      const missing = x.licensedPeople.filter((p) => p.email && !enrolled.has(p.email.toLowerCase()));
      add("training_enrollment", missing.length ? "flag" : "pass", "Training enrollment",
        missing.length ? `${enrolled.size} learners in Huntress against ${x.licensedPeople.length} licensed people in Microsoft 365. Not enrolled: ${missing.slice(0, 4).map((p) => p.name || p.email).join(", ")}${missing.length > 4 ? "…" : ""}.`
          : `Every licensed person in Microsoft 365 is enrolled (${enrolled.size}).`);
    }
  }
  add("phishing_present", x.phishing.length ? "pass" : "flag", "Phishing reporting",
    x.phishing.length ? (() => { const p = x.phishing[x.phishing.length - 1]!; return `${p.month ?? "Latest"} Managed Phishing report: ${p.reported ?? 0} reports on ${p.delivered ?? 0} simulated emails.`; })()
      : "No Huntress Managed Phishing report uploaded for the quarter.");
  add("tool_coverage", "later", "Security tools on every device", "Huntress, Sophos and ThreatLocker against the RMM list. Comes with ReviewHub step 4.");
  return out;
}

/** Training completion across the quarter's monthly reports (completed over enrolled, all months). */
export function trainingPct(learning: LearningParsed[]): number | null {
  const e = learning.reduce((s, l) => s + (l.enrolled ?? 0), 0), c = learning.reduce((s, l) => s + (l.completed ?? 0), 0);
  return pct(c, e);
}
